<!-- eslint-disable vue/no-v-html -->
<template>
  <v-timeline-item
    v-if="ACTIVITIES[data.type]"
    :dot-color="color"
    :icon="activity.icon"
    fill-dot
  >
    <v-expansion-panels
      v-model="panel"
      class="expansion-panels"
      variant="accordion"
      flat
    >
      <v-expansion-panel
        :disabled="!data?.details?.email?.messageId && !isNextEmail"
        @group:selected="
          (isNextEmail ? getNextMessage() : getMessage(), (panelExpanded = !panelExpanded))
        "
      >
        <v-expansion-panel-title>
          <v-row
            no-gutters
            align="center"
            class="d-flex justify-center"
            :class="{ 'flex-column': $vuetify.display.smAndDown }"
          >
            <v-col md="8">
              <div class="text-subtitle-1 text-black">
                {{ $t(activity.title) }}
              </div>
              <div class="text-grey text--small">
                <span v-if="data.details?.email?.type">{{ data.details.email.type }}</span>
                <span v-if="initiator">
                  {{ $t('lead-timeline.email-activity.initiated-by') }} {{ initiator }}</span
                >
                <span v-if="isNextEmail">
                  <span v-if="message?.scheduledAt"
                    >{{ $t('lead-timeline.email-activity.scheduled-at') }}:
                    {{ formatDate(message.scheduledAt) }}</span
                  >
                  <span v-else>{{ nextEmailMessage }}</span>
                </span>
              </div>
            </v-col>
            <v-col
              class="pr-2 text-right text-grey text--small"
              :class="{
                'text-start mt-2': $vuetify.display.smAndDown,
                'pr-8': !data?.details?.email?.messageId,
              }"
              md="4"
            >
              {{ formatDate(data.createdAt) }}
            </v-col>
          </v-row>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-row v-if="messageLoading === loadingStates.LOADING">
            <v-col class="text-center">
              <v-progress-circular
                indeterminate
                color="primary"
              />
            </v-col>
          </v-row>
          <v-row v-else-if="message">
            <v-col>
              <div class="d-flex justify-space-between align-center mb-2">
                <div>
                  <div v-if="isNextEmail">
                    {{ $t('lead-timeline.email-activity.next-email.title') }}:
                  </div>
                  <div v-else>{{ $t('lead-timeline.email-activity.title') }}:</div>
                </div>
                <v-btn
                  v-if="isNextEmail"
                  size="small"
                  color="primary"
                  variant="outlined"
                  @click="openEditDialog"
                >
                  <v-icon start>mdi-pencil</v-icon>
                  {{ $t('buttons.content-creator.edit') }}
                </v-btn>
              </div>
              <!-- Upselling Dialog -->
              <BlurredBackdropUpselling v-if="upsellingDialog" />
              <v-list density="compact">
                <v-list-item class="px-0">
                  <v-list-item-title
                    >{{ $t('lead-timeline.email-activity.subject') }}:</v-list-item-title
                  >
                  <div class="text-grey text--darken-3 text-body-2 rounded lighten-4 grey">
                    {{ message.subject }}
                  </div>
                </v-list-item>

                <v-list-item
                  v-if="message.attachments && message.attachments.length > 0"
                  class="px-0"
                >
                  <v-list-item-title>{{
                    $t('lead-timeline.email-activity.attachments')
                  }}</v-list-item-title>
                  <v-list
                    density="compact"
                    class="mt-2"
                  >
                    <v-list-item
                      v-for="(attachment, index) in sortedAttachments"
                      :key="attachment.id || index"
                      class="px-2 py-1"
                    >
                      <template v-slot:prepend>
                        <v-icon
                          size="small"
                          color="grey"
                          >mdi-file</v-icon
                        >
                      </template>
                      <v-list-item-title class="text-body-2">
                        {{ attachment.name }}
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-list-item>

                <v-list-item class="px-0">
                  <v-list-item-title>{{ $t('labels.text') }}</v-list-item-title>
                  <div class="text-grey text--darken-3 text-body-2">
                    <!-- Newsletter content rendered inline -->
                    <div
                      v-if="message.isNewsletter && message.htmlBody"
                      class="email-newsletter rounded lighten-4 grey px-2"
                      v-html="formatNewsletterHtml(message.htmlBody)"
                    />
                    <!-- Regular email in iframe -->
                    <iframe
                      v-else-if="message.htmlBody"
                      :srcdoc="formatIframeHtml(message.htmlBody)"
                      class="email-html-frame rounded lighten-4 grey"
                      frameborder="0"
                      sandbox="allow-popups allow-popups-to-escape-sandbox"
                    />
                    <!-- Plain text fallback -->
                    <div
                      v-else
                      class="text-caption email-text rounded lighten-4 grey"
                      v-html="formatText(message.textBody)"
                    />
                  </div>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
          <v-row v-else-if="!message && isNextEmail">
            <v-list density="compact">
              <v-list-item
                class="px-0"
                v-if="!data?.details?.emailOpened"
              >
                <v-list-item-title>
                  {{ $t('lead-timeline.email-activity.next-email.not-clicked') }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-row>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Edit Dialog -->
    <EditEmailDialog
      v-model="editDialog"
      :message="message"
      :contact-id="$route?.params?.id"
      @saved="handleMessageSaved"
    />
  </v-timeline-item>
</template>

<script>
import * as Sentry from '@sentry/vue'

import BlurredBackdropUpselling from '@/components/BlurredBackdropUpselling.vue'
import { isExpertUser } from '@/lib/productTypes'
import brandingMixin from '@/mixins/branding'

import { formatDate } from '../../../../lib/dateTime.js'
import { htmlReplace } from '../../../../lib/truncate.js'
import MESSAGE from '../queries/Message.gql'
import NEXT_MESSAGE from '../queries/NextMessage.gql'
import EditEmailDialog from './EditEmailDialog.vue'

const COLORS = {
  SYSTEM: 'systemActivity',
  MAKLER: 'userActivity',
  LEAD: 'leadActivity',
  NEXT: 'orange',
}

const ACTIVITIES = {
  EMAIL_SENT: () => ({
    icon: 'mdi-email-outline',
    title: 'lead-timeline.email-activity.activities.sent',
    type: 'SYSTEM',
  }),
  EMAIL_OPENED: () => ({
    icon: 'mdi-email-open',
    title: 'lead-timeline.email-activity.activities.opened',
    type: 'LEAD',
  }),
  EMAIL_BOUNCED: () => ({
    icon: 'mdi-email-off-outline',
    title: 'lead-timeline.email-activity.activities.bounced',
    type: 'SYSTEM',
  }),
  EMAIL_SPAM_COMPLAINT: () => ({
    icon: 'mdi-email-off-outline',
    title: 'lead-timeline.email-activity.activities.spam',
    type: 'LEAD',
  }),
  EMAIL_NEXT: () => ({
    icon: 'mdi-email-alert-outline',
    title: 'lead-timeline.email-activity.activities.next',
    type: 'NEXT',
  }),
}
const LOADING_STATES = {
  IDLE: 'IDLE',
  LOADING: 'LOADING',
  LOADED: 'LOADED',
}

export default {
  mixins: [brandingMixin],
  components: {
    BlurredBackdropUpselling,
    EditEmailDialog,
  },
  props: {
    data: { type: Object, default: null },
  },
  data() {
    return {
      message: null,
      messageLoading: LOADING_STATES.IDLE,
      loadingStates: LOADING_STATES,
      panelExpanded: false,
      editDialog: false,
      upsellingDialog: false,
      panel: null,
    }
  },
  computed: {
    nextEmailMessage() {
      if (this.messageLoading === LOADING_STATES.LOADING) {
        return this.$t('lead-timeline.email-activity.next-email-loading')
      }

      return this.message || this.messageLoading === LOADING_STATES.IDLE
        ? this.$t('lead-timeline.email-activity.next-scheduled')
        : this.$t('lead-timeline.email-activity.no-next-scheduled')
    },
    isNextEmail() {
      return this.data.type === 'EMAIL_NEXT'
    },
    activity() {
      return ACTIVITIES[this.data.type](this.data.details)
    },
    color() {
      return this.data.user ? COLORS.MAKLER : COLORS[this.activity.type]
    },
    initiator() {
      return {
        MAKLER: this.data.user
          ? `${this.data.user.firstname} ${this.data.user.lastname}`
          : this.$t('lead-timeline.email-activity.initiator'),
        BACKOFFICE_USER: `${this.readablePlatformName} ${this.$t('lead-timeline.email-activity.initiator')}`,
      }[this.data.initiator]
    },
    primaryColor() {
      return this.$vuetify.theme.global.current.colors.primary
    },
    /**
     * Get attachments (maintain array order)
     */
    sortedAttachments() {
      if (!this.message?.attachments) return []
      return [...this.message.attachments]
    },
    isExpertUser() {
      return isExpertUser(this.$auth.user)
    },
  },
  created() {
    this.ACTIVITIES = ACTIVITIES
  },
  beforeUnmount() {
    this.isMounted = false
  },
  mounted() {
    if (this.isNextEmail) {
      this.getNextMessage(true)
      this.hasInitialNextEmailOpen()
    }
  },
  methods: {
    hasInitialNextEmailOpen() {
      const { query } = this.$route
      const queryKey = 'timelineOpen'
      if (query[queryKey] === 'nextEmail') {
        this.$router.replace({ ...this.$route, query: undefined })
        this.panel = 0
      }
    },
    formatDate,
    formatText(text) {
      if (!text) return ''
      return text
        .replace(/\n/gi, '<br/>')
        .replace(
          /\[LINK\]/gi,
          '<div class="tooltip">[LINK]<span class="tooltiptext">' +
            this.$t('lead-timeline.email-activity.hide-link-text') +
            '</span></div>'
        )
    },
    formatNewsletterHtml(html) {
      if (!html) return ''
      return htmlReplace(html)
    },
    formatIframeHtml(html) {
      if (!html) return ''
      const scrollbarStyles = `
        <style>
          ::-webkit-scrollbar {
            width: 12px;
            -webkit-appearance: none;
            display: block;
          }
          ::-webkit-scrollbar-track {
            background: #f1f1f1;
          }
          ::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 6px;
            border: 3px solid #f1f1f1;
          }
          ::-webkit-scrollbar-thumb:hover {
            background: #555;
          }
          body {
            overflow-y: scroll;
          }
        </style>
      `
      return scrollbarStyles + htmlReplace(html)
    },

    async getMessage() {
      if (this.panelExpanded || !this.data.details.email.messageId || this.message) {
        return
      }
      this.messageLoading = LOADING_STATES.LOADING
      try {
        const {
          data: { message },
        } = await this.$apollo.query({
          query: MESSAGE,
          variables: { id: this.data.details.email.messageId },
        })
        this.message = message
      } catch (error) {
        this.message = null
        Sentry.captureException(error, {
          tags: {
            component: 'EmailActivity',
            action: 'getMessage',
          },
        })
      } finally {
        this.messageLoading = LOADING_STATES.LOADED
      }
    },
    async getNextMessage(initialLoad = false) {
      if (!initialLoad && (this.panelExpanded || this.messageLoading === LOADING_STATES.LOADING)) {
        return
      }

      this.messageLoading = LOADING_STATES.LOADING
      try {
        const {
          data: { nextMessage },
        } = await this.$apollo.query({
          query: NEXT_MESSAGE,
          variables: { contactId: this.$route?.params?.id },
        })

        if (!nextMessage) {
          this.message = null
          return
        }

        const { htmlBody, textBody, subject } = nextMessage
        if (!htmlBody && !textBody && !subject) {
          this.message = null
          return
        }
        this.message = nextMessage
      } catch (error) {
        this.message = null
        Sentry.captureException(error, {
          tags: {
            component: 'EmailActivity',
            action: 'getNextMessage',
          },
        })
      } finally {
        this.messageLoading = LOADING_STATES.LOADED
      }
    },
    /**
     * Open edit dialog
     */
    openEditDialog() {
      this.$tracking.event('Button', 'Click', 'Edit Next Email')
      if (!this.isExpertUser) {
        this.upsellingDialog = true
        return
      }

      this.editDialog = true
    },

    /**
     * Handle message saved event from EditEmailDialog
     */
    async handleMessageSaved(updatedMessage) {
      // Update message immediately from mutation response
      this.message = {
        ...this.message,
        ...updatedMessage,
      }
      // Also refetch to ensure we have the latest from the server
      await this.getNextMessage(true)
      this.$emit('message-updated')
    },
  },
  emits: ['message-updated'],
}
</script>

<style scoped>
.expansion-panels {
  margin: -24px -24px 0 -24px;
}

.email-html-frame,
.email-newsletter {
  border: 3px solid v-bind(primaryColor);
  max-height: 800px;
  overflow-y: scroll;
}

.email-html-frame {
  width: 100%;
  min-height: 400px;
} /* Non-scoped styles for scrollbar pseudo-elements - they don't work with scoped styles */
.email-html-frame::-webkit-scrollbar,
.email-newsletter::-webkit-scrollbar {
  width: 12px;
  -webkit-appearance: none;
  display: block;
}

.email-html-frame::-webkit-scrollbar-track,
.email-newsletter::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.email-html-frame::-webkit-scrollbar-thumb,
.email-newsletter::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 6px;
  border: 3px solid #f1f1f1;
}

.email-html-frame::-webkit-scrollbar-thumb:hover,
.email-newsletter::-webkit-scrollbar-thumb:hover {
  background: #555;
}
.email-text :deep(.tooltip) {
  position: relative;
  display: inline-block;
  border-bottom: 1px dotted black;
}

.email-text :deep(.tooltip .tooltiptext) {
  visibility: hidden;
  width: 150px;
  background-color: #555;
  color: #fff;
  text-align: center;
  border-radius: 6px;
  padding: 5px 0;
  position: absolute;
  z-index: 1;
  bottom: 125%;
  left: 50%;
  margin-left: -75px;
  opacity: 0;
  transition: opacity 0.5s;
}

.email-text :deep(.tooltip .tooltiptext::after) {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: #555 transparent transparent transparent;
}

.email-text :deep(.tooltip:hover .tooltiptext) {
  visibility: visible;
  opacity: 0.7;
}
</style>
